import React, { useState, useEffect } from 'react';
import { Save, ArrowLeft, X, Plus, Copy, Check } from 'lucide-react';

interface DynamicFormProps {
  template: {
    id: string;
    title: string;
    icon: React.ReactNode;
    sections?: {
      title: string;
      key: string;
      type: 'text' | 'list';
      placeholder?: string;
    }[];
  };
  onSubmit: (data: Record<string, any>) => void;
  initialData?: Record<string, any>;
  onBack: () => void;
  isViewMode?: boolean;
}

export default function DynamicForm({ 
  template, 
  onSubmit, 
  initialData, 
  onBack,
  isViewMode = false 
}: DynamicFormProps) {
  // Parse initial content into sections if editing
  const parseInitialContent = () => {
    if (!initialData?.content || !template?.sections) {
      return { formData: { content: initialData?.content || '' }, listItems: {} };
    }
    
    const sections = template.sections;
    const formData: Record<string, string> = {};
    const listItems: Record<string, string[]> = {};
    
    // For single-section templates like 'quick', use the entire content
    if (sections.length === 0) {
      formData.content = initialData.content;
      return { formData, listItems };
    }
    
    // For multi-section templates, split by section titles
    const contentSections = initialData.content.split('\n\n');
    
    contentSections.forEach(section => {
      // Get section title and content
      const titleMatch = section.match(/^(.*?):\s*\n([\s\S]*)/);
      if (!titleMatch) return;
      
      const [_, title, content] = titleMatch;
      
      // Find matching template section
      const templateSection = sections.find(s => s.title === title);
      if (!templateSection) return;
      
      if (templateSection.type === 'list') {
        // Parse bullet points into list items
        listItems[templateSection.key] = content
          .split('\n')
          .map(line => line.replace(/^[•\s]+/, '').trim())
          .filter(Boolean);
      } else {
        formData[templateSection.key] = content.trim();
      }
    });
    
    return { formData, listItems };
  };

  const { formData: initialFormData, listItems: initialListItems } = parseInitialContent();
  
  const [formData, setFormData] = useState<Record<string, string>>(initialFormData);
  const [listItems, setListItems] = useState<Record<string, string[]>>(initialListItems);
  const [newListItem, setNewListItem] = useState('');
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update form data when template or initial data changes
  useEffect(() => {
    const { formData: newFormData, listItems: newListItems } = parseInitialContent();
    setFormData(newFormData);
    setListItems(newListItems);
  }, [template.id, initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (submitting) return;
    
    try {
      setSubmitting(true);
      setError(null);

      // For single-section templates like 'quick', use the content directly
      if (!template.sections || template.sections.length === 0) {
        await onSubmit({
          content: formData.content || '',
          template_type: template.id,
          title: initialData?.title || template.title
        });
        return;
      }
      
      // For multi-section templates, combine sections with titles
      const content = template.sections.map(section => {
        const title = section.title;
        if (section.type === 'list') {
          const items = listItems[section.key] || [];
          if (items.length === 0) return null;
          return `${title}:\n${items.map(item => `• ${item}`).join('\n')}`;
        } else {
          const value = formData[section.key];
          if (!value) return null;
          return `${title}:\n${value}`;
        }
      })
      .filter(Boolean)
      .join('\n\n');

      await onSubmit({
        content,
        template_type: template.id,
        title: initialData?.title || template.title,
        ...initialData // Preserve other fields
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit entry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Function to copy content to clipboard
  const copyToClipboard = () => {
    let contentToCopy = '';

    // For single-section templates
    if (!template.sections || template.sections.length === 0) {
      contentToCopy = formData.content || '';
    } else {
      // For multi-section templates
      contentToCopy = template.sections
        .map(section => {
          const title = section.title;
          if (section.type === 'list') {
            const items = listItems[section.key] || [];
            if (items.length === 0) return null;
            return `${title}:\n${items.map(item => `• ${item}`).join('\n')}`;
          } else {
            const value = formData[section.key];
            if (!value) return null;
            return `${title}:\n${value}`;
          }
        })
        .filter(Boolean)
        .join('\n\n');
    }

    navigator.clipboard.writeText(contentToCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Update template type header with larger font size and copy button
  const TemplateHeader = () => (
    <div className="mb-6 pb-4 border-b border-mint-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-mint-50 rounded-lg">
            {template.icon}
          </div>
          <span className="text-xl font-medium text-mint-700">
            {template.title}
          </span>
        </div>
        {isViewMode && (
          <button
            onClick={copyToClipboard}
            className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-mint-50 text-mint-700 rounded-lg hover:bg-mint-100 transition-colors"
            title="Copy entry content"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                <span>Copy</span>
              </>
            )}
          </button>
        )}
      </div>
      {error && (
        <div className="mt-4 p-3 bg-bubblegum-50 text-bubblegum-700 rounded-lg text-sm border border-bubblegum-200">
          {error}
        </div>
      )}
    </div>
  );

  if (isViewMode) {
    // For single-section templates
    if (!template.sections || template.sections.length === 0) {
      return (
        <div className="space-y-8">
          <TemplateHeader />
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-br from-mint-50 to-mint-100/30 rounded-lg text-midnight-700 whitespace-pre-wrap border border-mint-200">
              {formData.content}
            </div>
          </div>
        </div>
      );
    }

    // For multi-section templates
    return (
      <div className="space-y-8">
        <TemplateHeader />
        {template.sections.map(section => {
          const content = section.type === 'text' ? formData[section.key] : listItems[section.key];
          if (!content || (Array.isArray(content) && content.length === 0)) return null;

          return (
            <div key={section.key} className="space-y-4">
              <h3 className="text-lg font-medium text-midnight-800">
                {section.title}
              </h3>

              {section.type === 'text' && (
                <div className="p-6 bg-gradient-to-br from-mint-50 to-mint-100/30 rounded-lg text-midnight-700 whitespace-pre-wrap border border-mint-200">
                  {formData[section.key]}
                </div>
              )}

              {section.type === 'list' && listItems[section.key]?.length > 0 && (
                <div className="space-y-2">
                  {listItems[section.key].map((item, index) => (
                    <div 
                      key={index} 
                      className="p-4 bg-gradient-to-br from-mint-50 to-mint-100/30 rounded-lg text-midnight-700 border border-mint-200 flex items-start space-x-3"
                    >
                      <div className="p-1 bg-mint-100 rounded-full flex-shrink-0">
                        <div className="w-1.5 h-1.5 rounded-full bg-mint-500" />
                      </div>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // If no sections defined, show simple text area
  if (!template.sections || template.sections.length === 0) {
    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <TemplateHeader />
        <textarea
          value={formData.content || ''}
          onChange={(e) => setFormData({ content: e.target.value })}
          placeholder="Write your entry here..."
          rows={10}
          className="w-full px-4 py-3 bg-white border border-mint-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
        />

        <div className="flex justify-between pt-4 border-t border-mint-200">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center space-x-2 px-6 py-2 text-midnight-600 hover:text-midnight-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </button>

          <button
            type="submit"
            disabled={submitting}
            className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-amber-500 to-marigold-500 text-white rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-all shadow-sm disabled:opacity-50"
          >
            <Save className="h-5 w-5" />
            <span>
              {submitting 
                ? 'Saving...' 
                : initialData 
                  ? 'Update Entry' 
                  : 'Save Entry'
              }
            </span>
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <TemplateHeader />
      {template.sections.map(section => (
        <div key={section.key} className="space-y-4">
          <h3 className="text-lg font-medium text-midnight-800">
            {section.title}
          </h3>

          {section.type === 'text' && section.key === 'subject' ? (
            // Single-line input for subject
            <input
              type="text"
              value={formData[section.key] || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                [section.key]: e.target.value
              }))}
              placeholder={section.placeholder}
              className="w-full px-4 py-3 bg-white border border-mint-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          ) : section.type === 'text' ? (
            // Multi-line textarea for other text fields
            <textarea
              value={formData[section.key] || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                [section.key]: e.target.value
              }))}
              placeholder={section.placeholder}
              rows={6}
              className="w-full px-4 py-3 bg-white border border-mint-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          ) : null}

          {section.type === 'list' && (
            <div className="space-y-3">
              {/* Existing Items */}
              {listItems[section.key]?.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <span className="flex-1 p-3 bg-mint-50 rounded-lg text-midnight-700">
                    {item}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const newItems = [...(listItems[section.key] || [])];
                      newItems.splice(index, 1);
                      setListItems(prev => ({ ...prev, [section.key]: newItems }));
                    }}
                    className="p-2 text-midnight-400 hover:text-bubblegum-600 hover:bg-bubblegum-50 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              ))}

              {/* Add New Item */}
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newListItem}
                  onChange={(e) => setNewListItem(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newListItem.trim()) {
                        setListItems(prev => ({
                          ...prev,
                          [section.key]: [...(prev[section.key] || []), newListItem.trim()]
                        }));
                        setNewListItem('');
                      }
                    }
                  }}
                  placeholder={section.placeholder}
                  className="flex-1 px-4 py-3 bg-white border border-mint-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (newListItem.trim()) {
                      setListItems(prev => ({
                        ...prev,
                        [section.key]: [...(prev[section.key] || []), newListItem.trim()]
                      }));
                      setNewListItem('');
                    }
                  }}
                  className="px-4 py-3 bg-mint-100 text-midnight-700 rounded-lg hover:bg-mint-200 transition-colors"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      <div className="flex justify-between pt-4 border-t border-mint-200">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center space-x-2 px-6 py-2 text-midnight-600 hover:text-midnight-800 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back</span>
        </button>

        <button
          type="submit"
          disabled={submitting}
          className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-amber-500 to-marigold-500 text-white rounded-lg hover:from-amber-400 hover:to-marigold-400 transition-all shadow-sm disabled:opacity-50"
        >
          <Save className="h-5 w-5" />
          <span>
            {submitting 
              ? 'Saving...' 
              : initialData 
                ? 'Update Entry' 
                : 'Save Entry'
            }
          </span>
        </button>
      </div>
    </form>
  );
}